import { useLogout } from '@h2hspark/auth-ui';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { useMemberSession } from '@/auth/auth-provider';
import { getWebEnvironment } from '@/env';

import { useSiteSettings } from '../domains/cms/api';
import { siteFooterGroups } from '../domains/cms/constants';
import {
  FOOTER_LINK_CLASS,
  FooterColumn,
  FooterLinkItem,
  SiteFooterChrome,
} from '../domains/cms/site-chrome-panel';
import { MEMBER_PROFILE_ROUTES } from '../domains/members/constants';

import { YOUR_EVENTS_DESTINATION } from './destinations';

/**
 * **The member app's footer, on desktop** — `Web App.dc.html` (10 September), lines 969–991.
 *
 * ## It is the marketing footer, with the app's own columns
 *
 * The prototype draws the in-app footer as the site's footer: the same band, wordmark, tagline,
 * social row, grid and rule. So this renders `SiteFooterChrome` — the CMS-driven component the
 * marketing site uses — and everything that is content still comes from `siteSettings`: the
 * tagline, the social links, the Company column, the copyright note and the location (`CMS-014`,
 * `CMS-017` AC-4). Two things cannot come from there, and they are the two columns written here:
 *
 *   - **Members** is the app's own navigation — Browse events, Your events, Connections, Your
 *     account — and **Log out**, which is an action rather than an address. The CMS holds links an
 *     editor types; a sign-out is not one, and the four destinations are the same routes the
 *     header carries, which a second copy in content would let drift. They are labels, the
 *     vocabulary of a component, not sentences.
 *   - **Organizers** points at the organizer console's own sign-up and sign-in, which live on
 *     another origin in development and behind `/organizer` when deployed. Only the build knows
 *     which, through `organizerUrl` (`env.ts`), so an editor could not type the right address.
 *     The prototype's four links all opened the for-organizers page — including *Organizer
 *     Login*, which `handoff-corrections.md` #133 corrects to the real sign-in — and its *Case
 *     studies* and *Pricing* are not built: that page no longer has case studies and never had
 *     pricing, and there are no plans in MVP (#137; Sumit's ruling of 2026-09-11, no plans).
 *
 * The site's own **Members** and **Organizers** groups are dropped from the CMS columns rather
 * than drawn twice — the site's Members group is anchors on the landing page and a *Log in* a
 * signed-in member does not need. They are matched by heading, which is the one place an
 * editor's rename would show: renaming "Members" in the console would draw it here as well. A
 * `siteSettings` field for the app's own groups would close that; it is proposed in the pull
 * request rather than added to the content type in a layout change.
 *
 * ## Desktop only
 *
 * The phone prototype draws no footer — the bottom bar is the app's chrome there, and every
 * Members link is one of its tabs. What a phone member does not reach from here is the Organizers
 * and Company columns; that is written down in `responsive-behaviour.ts`, as `PLT-008` AC-3
 * requires of anything a width takes away.
 */
export function MemberFooter(): React.ReactElement {
  const settings = useSiteSettings();
  const { session } = useMemberSession();
  const { organizerUrl } = getWebEnvironment();
  const data = settings.data?.data;
  const siteGroups = siteFooterGroups(data).filter(
    (group) => !APP_OWNED_FOOTER_HEADINGS.has(group.heading.trim().toLowerCase()),
  );

  return (
    <div className="hidden md:mt-14 md:block print:hidden">
      <SiteFooterChrome
        settings={data}
        withPromo={false}
        columns={
          <>
            <FooterColumn heading="Members">
              <FooterLinkItem href="/app" label="Browse events" />
              <FooterLinkItem href={YOUR_EVENTS_DESTINATION} label="Your events" />
              <FooterLinkItem href="/app/connections" label="Connections" />
              <FooterLinkItem href={MEMBER_PROFILE_ROUTES.account} label="Your account" />
              {session === null ? (
                <FooterLinkItem href={MEMBER_PROFILE_ROUTES.signIn} label="Log in" />
              ) : (
                <li>
                  <FooterLogOut />
                </li>
              )}
            </FooterColumn>
            <FooterColumn heading="Organizers">
              <FooterLinkItem
                href={organizerConsoleUrl(organizerUrl, '/start')}
                label="Host with H2HSpark"
              />
              <FooterLinkItem
                href={organizerConsoleUrl(organizerUrl, '/login')}
                label="Organizer login"
              />
            </FooterColumn>
            {siteGroups.map((group) => (
              <FooterColumn key={group.heading} heading={group.heading}>
                {group.links.map((item) => (
                  <FooterLinkItem
                    key={`${item.label}-${item.href}`}
                    href={item.href}
                    label={item.label}
                  />
                ))}
              </FooterColumn>
            ))}
          </>
        }
      />
    </div>
  );
}

/** The CMS groups this footer supplies itself, by heading (see the module header). */
export const APP_OWNED_FOOTER_HEADINGS: ReadonlySet<string> = new Set(['members', 'organizers']);

/**
 * An address in the organizer console: `${organizerUrl}/start` for self-serve sign-up
 * (`ORG-001` AC-1) and `${organizerUrl}/login` for sign-in (`USR-001`).
 *
 * Joined without a doubled slash whichever way the configured base is written, because the base
 * is an environment value and `https://h2hspark.com/organizer/` is as likely as its twin.
 */
export function organizerConsoleUrl(base: string, path: '/start' | '/login'): string {
  return `${base.replace(/\/+$/u, '')}${path}`;
}

/**
 * *Log out*, drawn as a footer link.
 *
 * `useLogout` from `@h2hspark/auth-ui` — the package owns what signing out is: `POST
 * /auth/logout`, then the stored token cleared whether or not the server answered. Not
 * `LogoutButton`, only because that component carries the auth forms' own link styling, which is
 * primary-coloured and underlined and would render purple-on-purple on this band. The failure is
 * swallowed for `LogoutButton`'s reason: the token is already gone, so the member is signed out
 * of this browser whatever the network did, and the redirect is unconditional.
 */
function FooterLogOut(): React.ReactElement {
  const logout = useLogout();
  const navigate = useNavigate();

  const signOut = async (): Promise<void> => {
    try {
      await logout.run();
    } catch {
      // See the docblock: the outcome the member asked for has already happened.
    } finally {
      navigate(MEMBER_PROFILE_ROUTES.signIn, { replace: true });
    }
  };

  return (
    <button
      type="button"
      onClick={() => {
        void signOut();
      }}
      disabled={logout.loading}
      className={`${FOOTER_LINK_CLASS} rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white`}
    >
      Log out
    </button>
  );
}
